import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useRef } from "react";
import { BuilderActionInput, GraphqlOperations } from "@src/types.generated";
import { whenSyncQueueIsClean } from "@src/features/builders/shared/autosave/builderActionsSync";
import { useSyncBuilderActionsStore } from "@src/features/libraryItem/hooks/useSyncBuilderActionsToServer";

export type PublishRefetchQuery =
  (typeof GraphqlOperations.Query)[keyof typeof GraphqlOperations.Query];

// A publish changes the item outside the action log, so scadmin records a
// publishedAction marker the poll delivers to every other open builder (the
// publishing tab's own clientId is skipped; it refreshes itself). The builder
// then refetches its item to show the new publish state.
export const isPublishedMarker = (
  payload: Omit<BuilderActionInput, "clientId" | "uuid">,
): boolean =>
  Boolean(
    payload.courseBuilderAction?.publishedAction ||
      payload.moduleBuilderAction?.publishedAction ||
      payload.skillBuilderAction?.publishedAction ||
      payload.trainingResourceBuilderAction?.publishedAction,
  );

/**
 * Returns the handler for a publishedAction marker: refetch `queries` once
 * the builder has no unsaved edits. A refetch under queued or in-flight
 * edits would replace them on screen with the server's copy, so it waits
 * until they have synced; markers that arrive meanwhile share the one
 * refetch.
 */
const useReloadOnPublish = (
  queries: readonly PublishRefetchQuery[],
): (() => void) => {
  const client = useApolloClient();
  const waitingRef = useRef<(() => void) | null>(null);
  useEffect(() => () => waitingRef.current?.(), []);
  return useCallback(() => {
    if (waitingRef.current) return;
    let done = false;
    const unsubscribe = whenSyncQueueIsClean(useSyncBuilderActionsStore, () => {
      done = true;
      waitingRef.current = null;
      void client.refetchQueries({ include: [...queries] });
    });
    if (!done) waitingRef.current = unsubscribe;
  }, [client, queries]);
};

export default useReloadOnPublish;
